import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { Box, IconButton, Checkbox, Flex } from '@chakra-ui/react';
import Select, { components } from 'react-select';

import { CloseIcon, ChevronDownIcon, SearchOutlineIcon } from 'Asset/icon';
import { Condition, Element } from 'Component/others';
import Tooltip from 'UiKit/tooltip';
import { TextRegular } from 'UiKit/typography';

import { reactSelectStyle } from './const';
import styles from './styles.module.css';
import SkeletonComponent from 'UiKit/skeleton';

const FilterSelect = ({
  className,
  clearable,
  field,
  isDisabled,
  isMulti,
  counterLabel,
  margin,
  onChange,
  options = [],
  placeholder,
  value,
  width,
  height = '34px',
  isLoading,
  isSearchable = true,
}) => {
  const isAllowReset = !isEmpty(value) && clearable;

  const handleReset = (e) => {
    e.stopPropagation();

    if (onChange) onChange(null);
    else if (field) field.onChange(null);
  };

  const handleOnChange = (selectedOption) => onChange(selectedOption);

  return (
    <Box
      margin={margin}
      minWidth={width}
      className={`${className ?? ''} 
      ${styles['select-component__container']} 
      ${isDisabled ? styles['select-component__container--disabled'] : ''} `}
    >
      <Condition statement={!isLoading} fallback={<SkeletonComponent width={width} height={height} />}>
        <Select
          {...field}
          isSearchable={isSearchable}
          value={value}
          options={options}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isMulti={isMulti}
          hideSelectedOptions={false}
          isClearable={false}
          isOptionSelected={true}
          onChange={handleOnChange}
          styles={{
            control: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.control }),
            indicatorSeparator: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.indicatorSeparator }),
            menu: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.menu }),
            menuList: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.menuList }),
            multiValue: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.multiValue }),
            placeholder: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.placeholder }),
            singleValue: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.singleValue }),
            noOptionsMessage: (baseStyles) => ({ ...baseStyles, ...reactSelectStyle.noOptionsMessage }),
          }}
          components={{
            DropdownIndicator: (props) => <DropdownIndicator {...props} isSearchable={isSearchable} />,
            Option: (props) => <CustomOption {...props} isMulti={isMulti} />,
            ValueContainer: (props) => <CustomMultiValue value={value} counterLabel={counterLabel} props={props} />,
          }}
        />

        <Condition statement={isAllowReset}>
          <IconButton icon={<CloseIcon />} className={styles['button__reset']} colorScheme="gray" variant="ghost" size="xs" onClick={handleReset} />
        </Condition>
      </Condition>
    </Box>
  );
};

const MemoizedFilterSelect = (props) => {
  return useMemo(
    () => <FilterSelect {...props} />,
    /* eslint-disable */
    [props?.value, props?.options, props?.isLoading, props?.onChange],
  );
};

export default MemoizedFilterSelect;

const CustomOption = ({ children, isMulti, ...props }) => {
  return (
    <Box
      {...props.innerProps}
      className={styles['option__container']}
      style={{ backgroundColor: props.isFocused ? 'var(--color-semantic-background)' : 'transparent' }}
    >
      <Condition statement={isMulti}>
        <Checkbox size="lg" isChecked={props.isSelected} />
      </Condition>

      <Box className={styles['option__box--text']}>
        <Tooltip label={children} placement="top" hasArrow color="var(--color-semantic-white)">
          <TextRegular className={styles['option__text']}>{children}</TextRegular>
        </Tooltip>
        <Condition statement={props?.data?.labelDescription}>
          <TextRegular className={styles['option__text--description']}>{`${props?.data?.labelDescription}`}</TextRegular>
        </Condition>
      </Box>
    </Box>
  );
};

const CustomMultiValue = ({ value, counterLabel, props }) => {
  return (
    <components.ValueContainer {...props} className={styles['customer-multi-value__container']}>
      <Flex className={styles['multi-value__container']}>
        <Condition statement={value?.length}>
          <Box className={styles['multi-value__counter']}>{value?.length}</Box>
          <TextRegular className={styles['multi-value__label']}>{counterLabel}</TextRegular>
        </Condition>

        <Element value={props?.children} />
      </Flex>
    </components.ValueContainer>
  );
};

const DropdownIndicator = ({ innerProps, selectProps, isSearchable }) => {
  return (
    <Box {...innerProps} style={{ padding: '8px' }}>
      <Condition statement={selectProps?.menuIsOpen && isSearchable} fallback={<ChevronDownIcon width="18px" height="18px" color="var(--color-neutral40)" />}>
        <SearchOutlineIcon width="14px" height="14px" />
      </Condition>
    </Box>
  );
};
